import { type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

interface PageContainerProps {
  title: string;
  description?: string;
  actions?: ReactNode;
  /**
   * Shows a back control to the LEFT of the title.
   *
   * `true` goes back in history; a string navigates to that route, which is
   * what you want when the page can be reached directly by URL and there is no
   * history to pop. Back belongs here rather than in `actions` — actions render
   * on the right, where a back button reads as just another operation and is
   * the last place anyone looks for it.
   */
  backTo?: true | string;
  children: ReactNode;
}

export function PageContainer({ title, description, actions, backTo, children }: PageContainerProps) {
  const navigate = useNavigate();

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex min-w-0 items-center gap-3">
          {backTo && (
            <button
              type="button"
              aria-label="Go back"
              onClick={() => (backTo === true ? navigate(-1) : navigate(backTo))}
              className="flex h-9 w-9 flex-none items-center justify-center rounded-md border border-input bg-background text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
          )}
          <div className="min-w-0">
            <h1 className="truncate text-2xl font-bold tracking-tight">{title}</h1>
            {description && (
              <p className="text-muted-foreground">{description}</p>
            )}
          </div>
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {children}
    </div>
  );
}
